import { requestJson } from "@/lib/api";

/** Modo de acesso da unidade: compartilhado (padrão) ou isolado por conta de unidade. */
export type DeliveryUnitAccessMode = "ACESSO_COMPARTILHADO" | "ACESSO_INDEPENDENTE";

export interface DeliveryUnit {
  id: string;
  customerId: string;
  externalCode: string;
  name: string;
  address: string | null;
  district: string | null;
  city: string | null;
  postalCode: string | null;
  deliveryWindowText: string | null;
  email: string | null;
  active: boolean;
  /** Tabela de preços própria da unidade (override da do cliente); null = usa a tabela do cliente. */
  priceTableId: string | null;
  accessMode: DeliveryUnitAccessMode;
}

export interface DeliveryUnitInput {
  externalCode: string;
  name: string;
  address?: string | null;
  district?: string | null;
  city?: string | null;
  postalCode?: string | null;
  deliveryWindowText?: string | null;
  email?: string | null;
  /** Nulo/ausente mantém o valor atual (o backend só altera quando enviado). */
  accessMode?: DeliveryUnitAccessMode | null;
}

export async function fetchDeliveryUnits(customerId: string): Promise<DeliveryUnit[]> {
  return requestJson<DeliveryUnit[]>(`/api/v1/admin/customers/${customerId}/delivery-units`);
}

export async function createDeliveryUnit(customerId: string, input: DeliveryUnitInput): Promise<DeliveryUnit> {
  return requestJson<DeliveryUnit>(`/api/v1/admin/customers/${customerId}/delivery-units`, {
    method: "POST",
    body: input,
  });
}

export async function updateDeliveryUnit(
  customerId: string,
  unitId: string,
  input: DeliveryUnitInput,
): Promise<DeliveryUnit> {
  return requestJson<DeliveryUnit>(`/api/v1/admin/customers/${customerId}/delivery-units/${unitId}`, {
    method: "PUT",
    body: input,
  });
}

export async function deactivateDeliveryUnit(customerId: string, unitId: string): Promise<DeliveryUnit> {
  return requestJson<DeliveryUnit>(`/api/v1/admin/customers/${customerId}/delivery-units/${unitId}/deactivate`, {
    method: "POST",
  });
}

export async function reactivateDeliveryUnit(customerId: string, unitId: string): Promise<DeliveryUnit> {
  return requestJson<DeliveryUnit>(`/api/v1/admin/customers/${customerId}/delivery-units/${unitId}/reactivate`, {
    method: "POST",
  });
}

/**
 * Atribui (ou limpa, com `priceTableId = null`) a tabela de preços própria da unidade. O backend valida
 * que a tabela pertence ao mesmo segmento do cliente.
 */
export async function assignDeliveryUnitPriceTable(
  customerId: string,
  unitId: string,
  priceTableId: string | null,
): Promise<DeliveryUnit> {
  return requestJson<DeliveryUnit>(`/api/v1/admin/customers/${customerId}/delivery-units/${unitId}/price-table`, {
    method: "PUT",
    body: { priceTableId },
  });
}

// --- Logins exclusivos da unidade (acessos) ---------------------------------------------------------

export interface DeliveryUnitLogin {
  id: string;
  deliveryUnitId: string;
  email: string;
  active: boolean;
  passwordResetRequired: boolean;
}

/** Resposta de criação/reset: a senha temporária aparece UMA vez para o admin comunicar ao cliente. */
export interface DeliveryUnitLoginCreated {
  id: string;
  deliveryUnitId: string;
  email: string;
  temporaryPassword: string;
}

const accessBase = (customerId: string, unitId: string) =>
  `/api/v1/admin/customers/${customerId}/delivery-units/${unitId}/access`;

export function fetchUnitLogins(customerId: string, unitId: string): Promise<DeliveryUnitLogin[]> {
  return requestJson<DeliveryUnitLogin[]>(accessBase(customerId, unitId));
}

export function createUnitLogin(customerId: string, unitId: string, email: string): Promise<DeliveryUnitLoginCreated> {
  return requestJson<DeliveryUnitLoginCreated>(accessBase(customerId, unitId), {
    method: "POST",
    body: { email },
  });
}

export function resetUnitLoginPassword(
  customerId: string,
  unitId: string,
  accessId: string,
): Promise<DeliveryUnitLoginCreated> {
  return requestJson<DeliveryUnitLoginCreated>(`${accessBase(customerId, unitId)}/${accessId}/reset-password`, {
    method: "POST",
  });
}

export function deactivateUnitLogin(customerId: string, unitId: string, accessId: string): Promise<DeliveryUnitLogin> {
  return requestJson<DeliveryUnitLogin>(`${accessBase(customerId, unitId)}/${accessId}/deactivate`, { method: "POST" });
}

export function reactivateUnitLogin(customerId: string, unitId: string, accessId: string): Promise<DeliveryUnitLogin> {
  return requestJson<DeliveryUnitLogin>(`${accessBase(customerId, unitId)}/${accessId}/reactivate`, { method: "POST" });
}

// --- Backfill administrado (conversão do titular) ---------------------------------------------------

export interface UnitLoginBackfillUnitEntry {
  unitId: string;
  unitName: string;
  action: "ALREADY_HAS_LOGIN" | "WOULD_CREATE" | "CREATED" | "BLOCKED_EMAIL_COLLISION";
  email: string | null;
  temporaryPassword: string | null;
}

export interface UnitLoginBackfillReport {
  customerId: string;
  dryRun: boolean;
  titularConverted: boolean;
  titularWouldConvert: boolean;
  units: UnitLoginBackfillUnitEntry[];
  warnings: string[];
}

export function backfillUnitLogins(customerId: string, dryRun: boolean): Promise<UnitLoginBackfillReport> {
  return requestJson<UnitLoginBackfillReport>(
    `/api/v1/admin/customers/${customerId}/unit-logins/backfill?dryRun=${dryRun}`,
    { method: "POST" },
  );
}
